import React from 'react';
import { Slot } from '../../utils/autoplan';

interface AutoplanCardProps {
  autoplanLoading: boolean;
  autoplanError: string | null;
  autoplanResults: Slot[];
  selectedSlot: Slot | null;
  selectedConnectionData: any;
  proposalSlots: { start: string; end: string }[];
  proposalMessage: string;
  onRunAutoplan: () => void;
  onSetProposalSlots: React.Dispatch<React.SetStateAction<{ start: string; end: string }[]>>;
  onSetProposalMessage: React.Dispatch<React.SetStateAction<string>>;
  onSubmitProposal: () => void;
  onCreateEventFromSlot: (slot: Slot) => void;
  onHandleConnectionCTA: (action: any) => void;
  hasActiveScheduled: (data: any) => boolean;
}

const AutoplanCard: React.FC<AutoplanCardProps> = ({
  autoplanLoading,
  autoplanError,
  autoplanResults,
  selectedSlot,
  selectedConnectionData,
  proposalSlots,
  proposalMessage,
  onRunAutoplan,
  onSetProposalSlots,
  onSetProposalMessage,
  onSubmitProposal,
  onCreateEventFromSlot,
  onHandleConnectionCTA,
  hasActiveScheduled,
}) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-lg">🤖</span>
          <div className="font-medium text-blue-800">Autoplan suggesties</div>
        </div>
        <button
          onClick={onRunAutoplan}
          disabled={autoplanLoading}
          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {autoplanLoading ? 'Bezig...' : 'Vind suggesties'}
        </button>
      </div>

      {hasActiveScheduled(selectedConnectionData) && (
        <div className="mt-2">
          <button
            onClick={() => onHandleConnectionCTA('agenda')}
            className="px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"
          >
            Open planning
          </button>
        </div>
      )}

      {!autoplanError && autoplanResults.length === 0 && !autoplanLoading && (
        <div className="text-xs text-gray-600">Nog geen suggesties. Klik "Vind suggesties".</div>
      )}

      {autoplanError && !hasActiveScheduled(selectedConnectionData) && (
        <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 space-y-2">
          <div className="font-medium">Voorstel doen</div>
          <p>Stuur 1–3 tijden als voorstel.</p>
          {proposalSlots.map((slot, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-2">
              <input
                type="datetime-local"
                value={slot.start}
                onChange={(e) => {
                  const next = [...proposalSlots];
                  next[idx] = { ...next[idx], start: e.target.value };
                  onSetProposalSlots(next);
                }}
                className="border rounded px-2 py-1 text-xs"
              />
              <input
                type="datetime-local"
                value={slot.end}
                onChange={(e) => {
                  const next = [...proposalSlots];
                  next[idx] = { ...next[idx], end: e.target.value };
                  onSetProposalSlots(next);
                }}
                className="border rounded px-2 py-1 text-xs"
              />
            </div>
          ))}
          {proposalSlots.length < 3 && (
            <button
              onClick={() => onSetProposalSlots((prev) => [...prev, { start: '', end: '' }])}
              className="text-blue-600 text-xs hover:underline"
            >
              + Slot toevoegen
            </button>
          )}
          <textarea
            placeholder="Bericht (optioneel)"
            value={proposalMessage}
            onChange={(e) => onSetProposalMessage(e.target.value)}
            className="w-full border rounded px-2 py-1 text-xs"
            rows={2}
          />
          <div className="flex space-x-2">
            <button
              onClick={onSubmitProposal}
              className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
            >
              Verstuur voorstel
            </button>
            <button
              onClick={() => {
                onSetProposalSlots([{ start: '', end: '' }]);
                onSetProposalMessage('');
              }}
              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {autoplanResults.length > 0 && (
        <div className="space-y-2">
          {autoplanResults.map((slot, idx) => (
            <div
              key={`${slot.start.getTime()}-${idx}`}
              className={`flex items-center justify-between rounded border p-2 text-sm ${
                selectedSlot === slot ? 'border-blue-400 bg-white' : 'border-blue-100 bg-white'
              }`}
            >
              <div className="text-gray-800">
                {slot.start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}{' '}
                {slot.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} -{' '}
                {slot.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <button
                onClick={() => onCreateEventFromSlot(slot)}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
              >
                Plan deze tijd
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500">
        Geen overlap? Klik "Vind suggesties" en kies later "Voorstel doen" (coming next).
      </div>
    </div>
  );
};

export default AutoplanCard;






